import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Skull, X, Loader2, CheckCircle2, Globe, Users, Copy, ArrowDown, Shuffle, Volume2, VolumeX, Share2, Trophy, Shield, Film } from "lucide-react";

/*  Cinematic landing hero — the first thing every visitor sees.
    One-per-session (sessionStorage key `hero_seen_v1`).
    Replace `HERO_VIDEO_URL` with your real video URL when it's cut.
    When set, the <video> tag auto-plays muted behind the overlay instead
    of the text sequence; the beats + CTA still ride on top so the hero
    experience is identical.
*/

const HERO_VIDEO_URL = "";   // ← drop your MP4/webm URL here when ready
// Hero portrait (served from backend). Shown behind beats + on final CTA.
const HERO_PORTRAIT_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api/nye/kairo.png";

// Lightweight analytics fire-and-forget. Never blocks the cinematic.
const fireEvent = (kind, detail = "") => {
  try { api.post("/nye/event", { kind, detail }).catch(() => {}); } catch {}
};

// Hero beat strings in 7 languages — picked by browser locale.
const HERO_I18N = {
  en: { b1_text:"Same New Year's.", b1_sub:"Every year.",
        b2_text:"\"Same party… again.\"", b2_sub:"— every group chat on Earth",
        b3_text:"\"We always do this.\"", b3_sub:"",
        b4_text:"\"Anyone even here?\"",  b4_sub:"",
        b5_text:"One world. One killer.", b5_sub:"",
        b6_text:"Meet Kairo.", b6_sub:"The first detective you'll stand with.",
        b7_text:"One case. Three waves. Thousands of detectives.", b7_sub:"",
        b8_text:"Bring your crew. Fight for your country.", b8_sub:"",
        b9_text:"One world. One killer. Seven billion suspects.", b9_sub:"" },
  es: { b1_text:"El mismo Año Nuevo.", b1_sub:"Todos los años.",
        b2_text:"«La misma fiesta… otra vez.»", b2_sub:"— cada grupo del mundo",
        b3_text:"«Siempre hacemos esto.»", b3_sub:"",
        b4_text:"«¿Hay alguien aquí?»", b4_sub:"",
        b5_text:"Un mundo. Un asesino.", b5_sub:"",
        b6_text:"Conoce a Kairo.", b6_sub:"El primer detective a tu lado.",
        b7_text:"Un caso. Tres oleadas. Miles de detectives.", b7_sub:"",
        b8_text:"Trae a tu equipo. Pelea por tu país.", b8_sub:"",
        b9_text:"Un mundo. Un asesino. Siete mil millones de sospechosos.", b9_sub:"" },
  pt: { b1_text:"O mesmo Ano-Novo.", b1_sub:"Todo ano.",
        b2_text:"«A mesma festa… de novo.»", b2_sub:"— todo grupo do mundo",
        b3_text:"«Sempre fazemos isso.»", b3_sub:"",
        b4_text:"«Tem alguém aqui?»", b4_sub:"",
        b5_text:"Um mundo. Um assassino.", b5_sub:"",
        b6_text:"Conheça Kairo.", b6_sub:"O primeiro detetive do seu lado.",
        b7_text:"Um caso. Três ondas. Milhares de detetives.", b7_sub:"",
        b8_text:"Chame sua crew. Lute pelo seu país.", b8_sub:"",
        b9_text:"Um mundo. Um assassino. Sete bilhões de suspeitos.", b9_sub:"" },
  fr: { b1_text:"Le même Nouvel An.", b1_sub:"Chaque année.",
        b2_text:"«La même fête… encore.»", b2_sub:"— tous les groupes du monde",
        b3_text:"«On fait toujours ça.»", b3_sub:"",
        b4_text:"«Y a quelqu'un ?»", b4_sub:"",
        b5_text:"Un monde. Un tueur.", b5_sub:"",
        b6_text:"Voici Kairo.", b6_sub:"Le premier détective à tes côtés.",
        b7_text:"Une affaire. Trois vagues. Des milliers de détectives.", b7_sub:"",
        b8_text:"Rassemble ton équipe. Bats-toi pour ton pays.", b8_sub:"",
        b9_text:"Un monde. Un tueur. Sept milliards de suspects.", b9_sub:"" },
  de: { b1_text:"Dasselbe Neujahr.", b1_sub:"Jedes Jahr.",
        b2_text:"„Dieselbe Party… wieder.\"", b2_sub:"— jeder Chat auf der Welt",
        b3_text:"„Wir machen das immer.\"", b3_sub:"",
        b4_text:"„Ist überhaupt jemand hier?\"", b4_sub:"",
        b5_text:"Eine Welt. Ein Mörder.", b5_sub:"",
        b6_text:"Triff Kairo.", b6_sub:"Der erste Detektiv an deiner Seite.",
        b7_text:"Ein Fall. Drei Wellen. Tausende Detektive.", b7_sub:"",
        b8_text:"Hol deine Crew. Kämpfe für dein Land.", b8_sub:"",
        b9_text:"Eine Welt. Ein Mörder. Sieben Milliarden Verdächtige.", b9_sub:"" },
  ja: { b1_text:"いつもの大晦日。", b1_sub:"毎年同じ。",
        b2_text:"「また同じパーティー…」", b2_sub:"— 地球上のあらゆるグループチャット",
        b3_text:"「いつもこれだよね。」", b3_sub:"",
        b4_text:"「誰かいる?」", b4_sub:"",
        b5_text:"ひとつの世界。ひとりの犯人。", b5_sub:"",
        b6_text:"カイロと出会え。", b6_sub:"最初に共に立つ探偵。",
        b7_text:"一つの事件。三つの波。数千の探偵。", b7_sub:"",
        b8_text:"クルーを集めろ。国のために戦え。", b8_sub:"",
        b9_text:"ひとつの世界。ひとりの犯人。70億の容疑者。", b9_sub:"" },
  zh: { b1_text:"又是跨年夜。", b1_sub:"每年都一样。",
        b2_text:"「又是这个派对……」", b2_sub:"— 地球上的每一个群聊",
        b3_text:"「我们总是这样。」", b3_sub:"",
        b4_text:"「有人在吗?」", b4_sub:"",
        b5_text:"一个世界。一个凶手。", b5_sub:"",
        b6_text:"认识凯罗。", b6_sub:"你站在身边的第一位侦探。",
        b7_text:"一个案件。三波。数千名侦探。", b7_sub:"",
        b8_text:"叫上你的队伍。为你的国家而战。", b8_sub:"",
        b9_text:"一个世界。一个凶手。七十亿嫌疑人。", b9_sub:"" },
};
const HERO_LANG = (() => {
  if (typeof navigator === "undefined") return "en";
  const base = (navigator.language || "en").split("-")[0].toLowerCase();
  return HERO_I18N[base] ? base : "en";
})();
const T = HERO_I18N[HERO_LANG];

const BEATS = [
  { at: 300,   dur: 3200, text: T.b1_text, sub: T.b1_sub },
  { at: 3700,  dur: 2600, text: T.b2_text, sub: T.b2_sub },
  { at: 6500,  dur: 2600, text: T.b3_text, sub: T.b3_sub },
  { at: 9200,  dur: 2200, text: T.b4_text, sub: T.b4_sub },
  { at: 11600, dur: 3400, logo: true },                            // Kairo reveal
  { at: 15200, dur: 3000, text: T.b5_text, sub: T.b5_sub },
  { at: 18400, dur: 2800, text: T.b6_text, sub: T.b6_sub },
  { at: 21400, dur: 2800, text: T.b7_text, sub: T.b7_sub },
  { at: 24400, dur: 2600, text: T.b8_text, sub: T.b8_sub },
  { at: 27200, dur: 3400, text: T.b9_text, sub: T.b9_sub },
  { at: 30800, final: true },                                      // CTA screen
];

const TOTAL_MS = 35500;

// ISO alpha-2 country code → flag emoji via regional indicator symbols.
// Returns a globe for non-standard codes (e.g. the 3-letter "OMH" that
// snuck in from early seed data) or missing codes.
const countryToFlag = (code) => {
  const c = (code || "").toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return "🌐";
  return String.fromCodePoint(
    0x1F1E6 + c.charCodeAt(0) - 65,
    0x1F1E6 + c.charCodeAt(1) - 65,
  );
};

// Pre-written share captions, keyed by language. `{n}` → detective number,
// `{url}` → personal share URL, `{flag}` → country flag.
// Covers the 6 languages supported by the site's i18n system + English.
const SHARE_CAPTIONS = {
  en: [
    "I'm Detective #{n}{flag}. The whole world solves one murder on Jan 31. Pick a side: {url}",
    "Reserved my spot for the first Global Murder Mystery Night. Who's riding with my country? {url}",
    "Dec 31 warm-up. Jan 31 main event. Thousands of us. One killer. I'm in — are you? {url}",
    "I just became Detective #{n}. If you've ever wanted to solve a case with me, this is the one: {url}",
    "The world is playing. Are you? Reserve free → {url}",
  ],
  es: [
    "Soy el Detective #{n}{flag}. El mundo entero resuelve un asesinato el 31 de enero. Elige bando: {url}",
    "Reservé mi lugar para la primera Noche Global de Misterio. ¿Quién juega por mi país? {url}",
    "31 dic calentamiento. 31 ene evento principal. Miles de nosotros. Un asesino. Yo entro: {url}",
    "Acabo de ser Detective #{n}. Si alguna vez quisiste resolver un caso conmigo, es este: {url}",
    "El mundo está jugando. ¿Y tú? Reserva gratis → {url}",
  ],
  pt: [
    "Sou o Detetive #{n}{flag}. O mundo inteiro resolve um assassinato em 31 de janeiro. Escolha um lado: {url}",
    "Reservei meu lugar na primeira Noite Global de Mistério. Quem vai com o meu país? {url}",
    "31 dez aquecimento. 31 jan evento principal. Milhares de nós. Um assassino. Entrei: {url}",
    "Virei Detetive #{n}. Se um dia quis resolver um caso comigo, é esse: {url}",
    "O mundo está jogando. E você? Reserve grátis → {url}",
  ],
  fr: [
    "Je suis le Détective #{n}{flag}. Le monde entier résout un meurtre le 31 janvier. Choisis ton camp : {url}",
    "J'ai réservé ma place pour la première Nuit du Mystère Mondial. Qui joue pour mon pays ? {url}",
    "31 déc échauffement. 31 jan grand soir. Des milliers de nous. Un tueur. J'y suis : {url}",
    "Je suis devenu Détective #{n}. Si tu as déjà voulu résoudre une affaire avec moi, c'est celle-ci : {url}",
    "Le monde joue. Et toi ? Réserve gratuit → {url}",
  ],
  de: [
    "Ich bin Detective #{n}{flag}. Die ganze Welt löst am 31. Januar einen Mord. Wähle deine Seite: {url}",
    "Habe meinen Platz für die erste Globale Mystery-Nacht reserviert. Wer spielt für mein Land? {url}",
    "31. Dez Aufwärmen. 31. Jan Hauptevent. Tausende von uns. Ein Killer. Ich bin dabei: {url}",
    "Bin gerade Detective #{n} geworden. Wenn du jemals mit mir einen Fall lösen wolltest — der ist es: {url}",
    "Die Welt spielt. Du auch? Reserviere kostenlos → {url}",
  ],
  ja: [
    "探偵 #{n}{flag} です。1月31日、世界中が一つの殺人事件を解きます。あなたの国を選んで: {url}",
    "初の世界ミステリー・ナイトに予約済み。私の国で一緒に戦う人は? {url}",
    "12/31 ウォームアップ。1/31 本番。何千人もの探偵。一人の犯人。参戦します: {url}",
    "探偵 #{n} になりました。もし一緒に事件を解きたかったなら、これです: {url}",
    "世界が遊んでいる。あなたは? 無料予約 → {url}",
  ],
  zh: [
    "我是第 {n} 号侦探{flag}。1月31日,全世界一起破一桩谋杀案。选边站: {url}",
    "我已为首届全球谜之夜预留席位。谁和我的国家一起玩? {url}",
    "12月31日热身,1月31日主赛。数千名侦探,一名真凶。我参加: {url}",
    "我刚成为 {n} 号侦探。想和我一起破案?就是这个: {url}",
    "世界都在玩。你呢?免费预留 → {url}",
  ],
};

const resolveCaptionLang = () => {
  if (typeof navigator === "undefined") return "en";
  const base = (navigator.language || "en").split("-")[0].toLowerCase();
  return SHARE_CAPTIONS[base] ? base : "en";
};

const fillCaption = (tpl, { n, url, country }) => {
  const flag = country ? ` ${countryToFlag(country)}` : "";
  return tpl.replaceAll("{n}", n).replaceAll("{url}", url).replaceAll("{flag}", flag);
};

const SNOWFLAKES = Array.from({ length: 36 });

// --- Procedural ambient audio engine ----------------------------------------
// Uses Web Audio API — no asset files, no CDN flakiness. Three layers:
//   1. Snow crackle  — looped filtered noise (continuous from mount)
//   2. Clock tick    — short noise+osc blip, scheduled every 1s, rises in
//                      intensity through beats 2-4
//   3. Cinematic hit — one-shot bass boom + metallic shimmer at Kairo reveal
// Everything respects a persisted mute preference and starts muted
// (autoplay-safe). First unmute call also resumes the AudioContext.
class HeroAudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.crackle = null;    // continuous noise node graph
    this.tickTimer = null;
    this.hitFired = false;
    this.muted = true;
    this.started = false;
  }
  _ensureCtx() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;        // start fully silent
    this.master.connect(this.ctx.destination);
  }
  _startCrackle() {
    if (!this.ctx || this.crackle) return;
    // Noise buffer — 2s of pink-ish noise, looped.
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 2, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < d.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99 * b0 + 0.0555 * white;
      b1 = 0.96 * b1 + 0.2965 * white;
      b2 = 0.57 * b2 + 1.0526 * white;
      d[i] = (b0 + b1 + b2 + white * 0.1) * 0.11;
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const lp = this.ctx.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 1800;
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 220;
    const g = this.ctx.createGain();
    g.gain.value = 0.35;
    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(this.master);
    src.start();
    this.crackle = { src, g };
  }
  _tick(intensity = 1) {
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(2600, t);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 0.05);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18 * intensity, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    osc.connect(g); g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.1);
  }
  _hit() {
    if (!this.ctx || this.muted || this.hitFired) return;
    this.hitFired = true;
    const t = this.ctx.currentTime;
    // Low boom
    const o1 = this.ctx.createOscillator();
    o1.type = "sine";
    o1.frequency.setValueAtTime(90, t);
    o1.frequency.exponentialRampToValueAtTime(32, t + 1.6);
    const g1 = this.ctx.createGain();
    g1.gain.setValueAtTime(0.0001, t);
    g1.gain.exponentialRampToValueAtTime(0.85, t + 0.02);
    g1.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    o1.connect(g1); g1.connect(this.master);
    o1.start(t); o1.stop(t + 1.9);
    // Metallic shimmer — short noise burst band-passed high
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.8, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 5200; bp.Q.value = 1.2;
    const g2 = this.ctx.createGain();
    g2.gain.setValueAtTime(0.0001, t);
    g2.gain.exponentialRampToValueAtTime(0.4, t + 0.01);
    g2.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
    src.connect(bp); bp.connect(g2); g2.connect(this.master);
    src.start(t);
  }
  start() {
    if (this.started) return;
    this._ensureCtx();
    if (!this.ctx) return;
    this.started = true;
    this._startCrackle();
    // Clock tick every 1s, intensity ramps with elapsed time
    this.tickTimer = setInterval(() => {
      // intensity curve: calm at start, builds through tension beats
      const el = Date.now() - (this.mountedAt || Date.now());
      let intensity = 0;
      if (el > 3200 && el < 11600)       intensity = 0.4 + (el - 3200) / 8400 * 0.9;
      else if (el >= 11600 && el < 15000) intensity = 0;   // silence through reveal
      else if (el >= 15000)              intensity = 0.5;
      if (intensity > 0.05) this._tick(intensity);
    }, 1000);
  }
  setMounted(at) { this.mountedAt = at; }
  setMuted(muted) {
    this.muted = !!muted;
    if (!this.ctx) return;
    // Resume context on first user-gesture unmute
    if (!muted && this.ctx.state === "suspended") { this.ctx.resume().catch(() => {}); }
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(muted ? 0 : 0.55, t, 0.35);
  }
  triggerHit() { this._hit(); }
  destroy() {
    try { if (this.tickTimer) clearInterval(this.tickTimer); } catch {}
    try { this.crackle?.src?.stop(); } catch {}
    try { this.ctx?.close(); } catch {}
    this.ctx = null; this.master = null; this.crackle = null;
    this.tickTimer = null; this.started = false;
  }
}
// ----------------------------------------------------------------------------

const HeroCinematic = ({ onDismiss }) => {
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { reserved_count, detective_number, share_token }
  const [copied, setCopied] = useState(false);
  const [cardVariant, setCardVariant] = useState("portrait");
  const [captionIdx, setCaptionIdx] = useState(0);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [captionOrder, setCaptionOrder] = useState(null);  // array of indices from backend
  const [me, setMe] = useState(null);         // live detective profile (/me/{token})
  const [nativeShared, setNativeShared] = useState(false);
  const [chromaFlashKey, setChromaFlashKey] = useState(0);  // bumps to re-trigger CSS anim
  const [trailerMode, setTrailerMode] = useState(() => {
    try { return localStorage.getItem("hero_trailer_mode_v1") === "1"; } catch { return false; }
  });
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("hero_audio_muted_v1") !== "0"; } catch { return true; }
  });
  const startedAt = useRef(Date.now());
  const rafRef = useRef(null);
  const audioRef = useRef(null);
  const hitFiredRef = useRef(false);

  // Fetch stats + leaderboard early so the final beat renders instantly
  useEffect(() => {
    fireEvent("hero_view");
    api.get("/nye/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/nye/leaderboard").then((r) => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
    // Caption winner-rotation ordering (per-language, 7-day rolling)
    api.get(`/nye/caption-order?lang=${resolveCaptionLang()}`)
      .then((r) => setCaptionOrder(r.data?.order || null))
      .catch(() => {});
  }, []);

  // --- Audio lifecycle ------------------------------------------------------
  useEffect(() => {
    const eng = new HeroAudioEngine();
    eng.setMounted(startedAt.current);
    audioRef.current = eng;
    eng.start();
    // Honour persisted preference. Autoplay policy: silent engine is fine
    // even if ctx stays suspended; it resumes on first unmute click.
    eng.setMuted(muted);
    return () => eng.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    audioRef.current?.setMuted(muted);
    try { localStorage.setItem("hero_audio_muted_v1", muted ? "1" : "0"); } catch {}
  }, [muted]);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      fireEvent(next ? "audio_mute" : "audio_unmute");
      return next;
    });
  };

  const toggleTrailer = () => {
    setTrailerMode((t) => {
      const next = !t;
      try { localStorage.setItem("hero_trailer_mode_v1", next ? "1" : "0"); } catch {}
      fireEvent(next ? "trailer_mode_on" : "trailer_mode_off");
      return next;
    });
  };

  useEffect(() => {
    const tick = () => {
      if (!paused) setElapsed(Date.now() - startedAt.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused]);

  // Fire the cinematic hit exactly once, when we cross into the Kairo reveal beat
  useEffect(() => {
    if (!hitFiredRef.current && elapsed >= 11600 && elapsed < 15200) {
      hitFiredRef.current = true;
      audioRef.current?.triggerHit();
      setChromaFlashKey((k) => k + 1);        // one-frame RGB-split flash
    }
  }, [elapsed]);

  const dismiss = () => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("hero") !== "preview") {
        sessionStorage.setItem("hero_seen_v1", "1");
      }
    } catch {}
    fireEvent("hero_dismissed");
    onDismiss?.();
  };

  const reserve = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const country = (typeof navigator !== "undefined" && navigator.language)
        ? navigator.language.split("-").pop().toUpperCase().slice(0, 2) : "";
      // Pass along `?ref=NYE_<token>` so the referrer gets attribution.
      let ref_token = "";
      try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get("ref") || "";
        if (raw.startsWith("NYE_")) ref_token = raw.slice(4);
      } catch {}
      const { data } = await api.post("/nye/reserve", {
        email, name, country,
        origin_url: window.location.origin,
        ref_token,
      });
      fireEvent("reserve_submit", country || "");
      setDone({
        reserved_count: data.reserved_count,
        detective_number: data.detective_number,
        share_token: data.share_token,
      });
    } catch (err) {
      setBusy(false);
      // soft-fail: still show thank-you so a flaky network doesn't kill the moment
      setDone({ reserved_count: stats?.reserved_count ?? 1 });
    }
  };

  // Load live detective profile (country rank, founding, recruits) once we
  // have a share token; refresh every 60s so recruits counter stays current.
  useEffect(() => {
    if (!done?.share_token) return;
    let cancelled = false;
    const load = () => api.get(`/nye/me/${done.share_token}`)
      .then((r) => { if (!cancelled) setMe(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [done?.share_token]);

  const shareUrl = done?.share_token
    ? `${window.location.origin}/?ref=NYE_${done.share_token}`
    : "";
  const cardUrl = done?.share_token
    ? `${(process.env.REACT_APP_BACKEND_URL || "")}/api/nye/share/${done.share_token}.png?variant=${cardVariant}`
    : "";

  const CARD_VARIANTS = [
    { id: "portrait", label: "Portrait" },
    { id: "crew",     label: "Crew call" },
    { id: "clue",     label: "The clue" },
    { id: "map",      label: "World map" },
    { id: "mycrew",   label: "My crew" },
  ];

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      fireEvent("share_link_copy");
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  // One-tap native share — iOS/Android share-sheet with the card image when
  // the device supports it. Falls back silently to copy-link elsewhere.
  const nativeShareSupported = typeof navigator !== "undefined"
    && typeof navigator.share === "function";

  const nativeShare = async () => {
    if (!shareUrl) return;
    const text = currentCaption || "I'm in for the Global First Run. Join my crew: " + shareUrl;
    try {
      // Try to attach the share-card PNG as a file; many apps show it as a
      // preview before posting. If file-share unsupported, fall back to text+URL.
      let files;
      if (cardUrl && typeof navigator.canShare === "function") {
        try {
          const r = await fetch(cardUrl);
          if (r.ok) {
            const blob = await r.blob();
            const f = new File([blob], `detective-${done.detective_number}.png`,
                               { type: "image/png" });
            if (navigator.canShare({ files: [f] })) files = [f];
          }
        } catch { /* ignore — share text only */ }
      }
      const payload = files
        ? { files, text, url: shareUrl, title: "WasntMe.Party — Global First Run" }
        : { text, url: shareUrl, title: "WasntMe.Party — Global First Run" };
      await navigator.share(payload);
      fireEvent("native_share");
      setNativeShared(true);
      setTimeout(() => setNativeShared(false), 1800);
    } catch (err) {
      // Treat user-cancel as a no-op; everything else falls back to clipboard
      if (err && err.name !== "AbortError") copyShare();
    }
  };

  const captionLang = resolveCaptionLang();
  const captionPool = SHARE_CAPTIONS[captionLang] || SHARE_CAPTIONS.en;
  // Apply backend-driven winner ordering if available; fall back to hand order.
  const orderedIdx = (captionOrder && captionOrder.length === captionPool.length)
    ? captionOrder
    : captionPool.map((_, i) => i);
  const resolvedIdx = orderedIdx[captionIdx % orderedIdx.length];
  const currentCaption = done ? fillCaption(captionPool[resolvedIdx], {
    n: done.detective_number || 1,
    url: shareUrl,
    country: (navigator.language || "").split("-").pop().toUpperCase().slice(0, 2),
  }) : "";

  const copyCaption = async () => {
    if (!currentCaption) return;
    try {
      await navigator.clipboard.writeText(currentCaption);
      setCaptionCopied(true);
      // detail = "{lang}:{idx}" so the backend can self-tune ordering
      fireEvent("caption_copy", `${captionLang}:${resolvedIdx}`);
      setTimeout(() => setCaptionCopied(false), 1800);
    } catch { /* noop */ }
  };

  const activeBeat = BEATS.find((b) => elapsed >= b.at && elapsed < b.at + (b.dur || 4000));
  const atFinal = elapsed >= (BEATS[BEATS.length - 1].at - 400);
  const showSkip = elapsed > 1800;

  return (
    <div className={`fixed inset-0 z-[80] bg-[#050403] overflow-hidden ${trailerMode ? "hero-mode-trailer" : ""}`}
      data-testid="hero-cinematic"
      data-mode={trailerMode ? "trailer" : "cinema"}
      role="dialog" aria-label="Welcome cinematic">
      {/* Video layer (if provided) ------------------------------------------- */}
      {HERO_VIDEO_URL ? (
        <video
          src={HERO_VIDEO_URL}
          autoPlay muted playsInline loop={false}
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
      ) : (
        <>
          {/* City skyline backdrop — pure CSS */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908] via-[#1a0b0b] to-[#050403]" />
          <div className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 50% 110%, #3a1414 0%, transparent 55%)," +
                "radial-gradient(ellipse at 20% 90%, #d4af3722 0%, transparent 30%)," +
                "radial-gradient(ellipse at 80% 85%, #8b000044 0%, transparent 40%)",
            }} />

          {/* Snowfall — split into two parallax layers for depth */}
          {SNOWFLAKES.map((_, i) => {
            const near = i % 2 === 0;
            return (
              <span key={i}
                className={`hero-snow ${near ? "hero-snow-near" : "hero-snow-far"}`}
                style={{
                  left: `${(i * 2.78) % 100}%`,
                  animationDelay: `${(i * 0.37) % 7}s`,
                  animationDuration: near ? `${7 + (i % 5)}s` : `${11 + (i % 4)}s`,
                  opacity: 0.3 + ((i % 7) * 0.08),
                  width:  near ? `${2 + (i % 3)}px` : `${1 + (i % 2)}px`,
                  height: near ? `${2 + (i % 3)}px` : `${1 + (i % 2)}px`,
                }} />
            );
          })}

          {/* Clock vignette top-right (soft) */}
          <div className="absolute top-6 right-6 font-mono text-xs tracking-[0.3em] text-[#5a4040]/60 select-none">
            11:47:{String(Math.floor(elapsed / 1000) % 60).padStart(2, "0")}
          </div>
        </>
      )}

      {/* Letterbox bars — cinema opening, slide in from frame 1 */}
      <div className="absolute top-0 left-0 right-0 h-10 bg-black z-10 hero-letterbox-top" />
      <div className="absolute bottom-0 left-0 right-0 h-10 bg-black z-10 hero-letterbox-bottom" />

      {/* 35mm film grain + lens vignette — always on, pulls the whole thing together */}
      <div className="hero-vignette" aria-hidden="true" />
      <div className="hero-grain" aria-hidden="true" />

      {/* Trailer-mode-only VHS scanline overlay */}
      {trailerMode && <div className="hero-scanlines" aria-hidden="true" />}

      {/* One-frame chromatic flash — fires with the audio hit */}
      {chromaFlashKey > 0 && (
        <div key={`chroma-${chromaFlashKey}`} className="hero-chroma" aria-hidden="true" />
      )}

      {/* Audio mute toggle — top-left, pulses while muted to invite the click */}
      <button
        type="button"
        onClick={toggleMute}
        aria-pressed={!muted}
        aria-label={muted ? "Unmute cinematic audio" : "Mute cinematic audio"}
        className={`absolute top-14 left-6 z-30 h-10 w-10 flex items-center justify-center rounded-full border bg-black/40 backdrop-blur-sm transition-colors ${
          muted
            ? "border-[#d4af37] text-[#d4af37] hover:text-[#f4f0ec] hero-audio-pulse"
            : "border-[#2d2424] text-[#a39797] hover:text-[#f4f0ec] hover:border-[#d4af37]"
        }`}
        data-testid="hero-audio-toggle">
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>

      {/* Trailer mode toggle — bolder/grittier visual option */}
      <button
        type="button"
        onClick={toggleTrailer}
        aria-pressed={trailerMode}
        aria-label={trailerMode ? "Switch to cinema mode" : "Switch to trailer mode"}
        title={trailerMode ? "Trailer mode · ON" : "Trailer mode · OFF"}
        className={`absolute top-28 left-6 z-30 h-10 w-10 flex items-center justify-center rounded-full border bg-black/40 backdrop-blur-sm transition-colors ${
          trailerMode
            ? "border-[#8b0000] text-[#8b0000] hover:text-[#f4f0ec] hero-trailer-pulse"
            : "border-[#2d2424] text-[#a39797] hover:text-[#f4f0ec] hover:border-[#8b0000]"
        }`}
        data-testid="hero-trailer-toggle"
        data-state={trailerMode ? "on" : "off"}>
        <Film size={16} />
      </button>

      {/* Skip + progress */}
      <AnimatePresence>
        {showSkip && !atFinal && (
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={dismiss}
            className="absolute top-14 right-6 z-30 text-xs tracking-widest text-[#a39797] hover:text-[#f4f0ec] px-3 py-2 rounded border border-[#2d2424] hover:border-[#d4af37] transition-colors bg-black/40 backdrop-blur-sm"
            data-testid="hero-skip">
            SKIP →
          </motion.button>
        )}
      </AnimatePresence>
      <div className="absolute bottom-12 left-0 right-0 h-[2px] bg-[#2d2424]/40 z-20">
        <div className="h-full bg-[#d4af37] transition-all duration-200"
          style={{ width: `${Math.min(100, (elapsed / TOTAL_MS) * 100)}%` }} />
      </div>

      {/* Beats --------------------------------------------------------------- */}
      <div className="relative z-20 h-full w-full flex items-center justify-center px-6">
        <AnimatePresence mode="wait">
          {activeBeat?.logo && (
            <motion.div key="logo"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              className="relative flex flex-col items-center"
              data-testid="hero-logo-reveal">
              {/* Kairo portrait */}
              <div className="relative" style={{ height: "clamp(360px, 62vh, 580px)", aspectRatio: "9/16" }}>
                {/* Rim-light bloom behind him — fires with the audio hit */}
                <div className="hero-rimlight" aria-hidden="true" />
                <img src={HERO_PORTRAIT_URL} alt="Kairo"
                  className="hero-kenburns h-full w-full object-contain drop-shadow-[0_0_40px_rgba(139,0,0,0.6)] relative z-[2]"
                  onError={(e) => { e.target.style.display = "none"; }} />
                {/* Shards radiating from behind */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[3]">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <span key={i} className="hero-shard"
                      style={{
                        transform: `rotate(${i * 36}deg) translateX(60px)`,
                        animationDelay: `${0.5 + i * 0.06}s`,
                      }} />
                  ))}
                </div>
              </div>
              {/* Wordmark */}
              <div className="font-display text-2xl sm:text-4xl text-[#f4f0ec] tracking-wide mt-4" translate="no">
                WASNT<span className="text-[#8b0000]">ME</span>.PARTY
              </div>
              <div className="font-hand text-[#d4af37] text-sm sm:text-base mt-1 rotate-[-1deg]">
                a NightBlood Chronicles production
              </div>
            </motion.div>
          )}

          {activeBeat && !activeBeat.logo && !activeBeat.final && (
            <motion.div key={activeBeat.at}
              initial={{ opacity: 0, y: 14, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -14, filter: "blur(8px)" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center max-w-3xl">
              <div className="font-display text-3xl sm:text-5xl lg:text-6xl text-[#f4f0ec] leading-tight"
                data-testid="hero-beat-text">
                {activeBeat.text}
              </div>
              {activeBeat.sub && (
                <div className="font-hand text-[#d4af37] text-lg sm:text-xl mt-4 rotate-[-1.5deg]">
                  {activeBeat.sub}
                </div>
              )}
            </motion.div>
          )}

          {atFinal && !done && (
            <motion.div key="cta"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="w-full max-w-md"
              data-testid="hero-final">
              <div className="text-center mb-5">
                <div className="text-xs tracking-[0.3em] text-[#d4af37] mb-2">
                  GLOBAL FIRST RUN · THE PROVING NIGHT
                </div>
                <div className="font-display text-4xl sm:text-5xl text-[#f4f0ec] leading-tight">
                  One world. One killer.
                </div>
                <div className="font-mono text-[11px] text-[#a39797] mt-3 tracking-widest leading-5" translate="no">
                  DEC 31 · 2026 · 20:00 UTC · FREE · WORLDWIDE
                </div>
                {stats?.reserved_count > 0 && (
                  <div className="mt-4 text-xs text-[#a39797] flex items-center justify-center gap-4">
                    <span className="flex items-center gap-1" data-testid="hero-reserved-count">
                      <Users size={11} className="text-[#d4af37]" />
                      <span className="text-[#f4f0ec] font-bold">{stats.reserved_count}</span> reserved
                    </span>
                    {stats.country_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Globe size={11} className="text-[#d4af37]" />
                        <span className="text-[#f4f0ec] font-bold">{stats.country_count}</span>&nbsp;{stats.country_count === 1 ? "country" : "countries"}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <form onSubmit={reserve} className="card-noir p-5 border-[#d4af37] backdrop-blur-sm bg-black/60">
                <input type="email" required placeholder="Your email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-3 focus:outline-none focus:border-[#d4af37]"
                  data-testid="hero-reserve-email" autoFocus />
                <input placeholder="Your name (optional)"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-4 focus:outline-none focus:border-[#d4af37]"
                  data-testid="hero-reserve-name" />
                <button type="submit" disabled={busy}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                  data-testid="hero-reserve-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Skull size={14} />}
                  Reserve my spot — free
                </button>
                <button type="button" onClick={dismiss}
                  className="w-full text-center text-xs text-[#5a5050] hover:text-[#a39797] mt-3 transition-colors"
                  data-testid="hero-enter-site">
                  or enter the site →
                </button>
              </form>

              {/* Country leaderboard — live social proof */}
              {leaderboard.length > 0 && (
                <div className="mt-5 card-noir p-4 backdrop-blur-sm bg-black/50" data-testid="hero-leaderboard">
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3 text-center">
                    FIGHT FOR YOUR COUNTRY · LIVE TOP 5
                  </div>
                  <ol className="space-y-2">
                    {leaderboard.slice(0, 5).map((c, i) => (
                      <li key={c.country} className="flex items-center justify-between text-sm"
                          data-testid={`hero-lb-row-${i}`}>
                        <span className="flex items-center gap-3">
                          <span className={`font-mono text-xs w-5 ${i === 0 ? "text-[#d4af37]" : "text-[#5a5050]"}`}>
                            #{i + 1}
                          </span>
                          <span className="text-xl leading-none" aria-hidden="true">{countryToFlag(c.country)}</span>
                          <span className="text-[#f4f0ec] font-bold tracking-wider" translate="no">
                            {c.country}
                          </span>
                        </span>
                        <span className="text-xs text-[#a39797]">
                          <span className="text-[#f4f0ec] font-bold" translate="no">{c.count}</span> detective{c.count !== 1 ? "s" : ""}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <a href="/leaderboard"
                    className="block text-center text-[10px] tracking-widest text-[#a39797] hover:text-[#d4af37] transition-colors mt-3 pt-2 border-t border-[#2d2424]"
                    data-testid="hero-leaderboard-link-pre">
                    SEE THE FULL WORLD →
                  </a>
                </div>
              )}
            </motion.div>
          )}

          {atFinal && done && (
            <motion.div key="thanks"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="text-center max-w-md w-full"
              data-testid="hero-reserved-thanks">
              <CheckCircle2 size={44} className="text-[#4ade80] mx-auto mb-4" />
              <div className="font-display text-3xl sm:text-4xl text-[#f4f0ec] mb-1">
                You're in.
              </div>
              <div className="text-[#d4af37] font-mono text-sm mb-2" translate="no" data-testid="hero-detective-number">
                {done.detective_number ? `DETECTIVE #${done.detective_number}` : "WELCOME, DETECTIVE"}
              </div>

              {/* Achievement strip — honest zero states: badges only appear
                  when their underlying data is real. Never fabricated. */}
              {me && (me.is_founding || me.country_rank || me.recruits_count > 0) && (
                <div className="flex items-center justify-center gap-2 flex-wrap mb-4"
                  data-testid="hero-achievements">
                  {me.is_founding && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest text-[#d4af37] border border-[#d4af37]/60 bg-[#d4af37]/5 rounded-full px-2.5 py-1"
                      data-testid="hero-badge-founding">
                      <Shield size={10} /> FOUNDING
                    </span>
                  )}
                  {me.country_rank && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest text-[#f4f0ec] border border-[#2d2424] rounded-full px-2.5 py-1"
                      data-testid="hero-badge-country-rank">
                      <Globe size={10} /> {me.country} · RANK #{me.country_rank}
                    </span>
                  )}
                  {me.recruits_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest text-[#4ade80] border border-[#4ade80]/40 bg-[#4ade80]/5 rounded-full px-2.5 py-1"
                      data-testid="hero-badge-recruits">
                      <Users size={10} /> {me.recruits_count} RECRUITED
                    </span>
                  )}
                </div>
              )}

              <div className="text-[#a39797] mb-5 text-sm">
                Confirmation email sent. Save or screenshot the card below and
                share — every friend you bring stands in your country's crew.
              </div>

              {cardUrl && (
                <>
                  <img src={cardUrl} alt={`Detective ${done.detective_number} card`}
                    className="w-full max-w-xs mx-auto rounded-md border border-[#2d2424] shadow-[0_0_30px_rgba(212,175,55,0.25)] mb-3"
                    data-testid="hero-share-card-img" />
                  <div className="flex items-center gap-1.5 justify-center flex-wrap mb-4"
                    data-testid="hero-card-variants">
                    {CARD_VARIANTS.map((v) => (
                      <button key={v.id} onClick={() => {
                        setCardVariant(v.id);
                        fireEvent("variant_switch", v.id);
                      }}
                        className={`text-[11px] tracking-widest px-2.5 py-1 rounded border transition-colors ${
                          cardVariant === v.id
                            ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                            : "border-[#2d2424] text-[#a39797] hover:text-[#f4f0ec] hover:border-[#5a5050]"
                        }`}
                        data-testid={`hero-card-variant-${v.id}`}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {shareUrl && (
                <div className="flex items-center gap-2 justify-center flex-wrap mb-4">
                  {nativeShareSupported && (
                    <button onClick={nativeShare} className="btn-primary text-xs"
                      data-testid="hero-native-share">
                      {nativeShared ? <CheckCircle2 size={12} className="text-green-400" /> : <Share2 size={12} />}
                      {nativeShared ? "Shared" : "Share…"}
                    </button>
                  )}
                  <button onClick={copyShare} className="btn-ghost text-xs"
                    data-testid="hero-copy-share">
                    {copied ? <CheckCircle2 size={12} className="text-green-400" /> : <Copy size={12} />}
                    {copied ? "Link copied" : "Copy share link"}
                  </button>
                  <a href={cardUrl} download={`detective-${done.detective_number}-${cardVariant}.png`}
                    onClick={() => fireEvent("card_download", cardVariant)}
                    className="btn-ghost text-xs" data-testid="hero-download-card">
                    <ArrowDown size={12} /> Save card
                  </a>
                </div>
              )}

              {/* My Crew — read-only count, no points or ranking impact */}
              {me && typeof me.recruits_count === "number" && (
                <div className="card-noir p-3 backdrop-blur-sm bg-black/50 text-center mb-4"
                  data-testid="hero-my-crew">
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-1">MY CREW</div>
                  <div className="text-sm text-[#f4f0ec]">
                    {me.recruits_count === 0
                      ? <>No recruits yet. Share your link and your crew will start showing up here.</>
                      : <>You've recruited <span className="font-bold text-[#4ade80]">{me.recruits_count}</span>{" "}
                        detective{me.recruits_count === 1 ? "" : "s"}.</>}
                  </div>
                </div>
              )}

              {/* Tap-to-share captions, pre-written in viewer's language */}
              {currentCaption && (
                <div className="card-noir p-4 backdrop-blur-sm bg-black/60 text-left mb-4"
                  data-testid="hero-caption-picker">
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">
                    SHARE SCRIPT · {captionLang.toUpperCase()}
                  </div>
                  <div className="text-sm text-[#f4f0ec] leading-relaxed mb-3 whitespace-pre-wrap"
                    data-testid="hero-caption-text">
                    {currentCaption}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={copyCaption} className="btn-primary text-xs"
                      data-testid="hero-copy-caption">
                      {captionCopied
                        ? <CheckCircle2 size={12} className="text-green-400" />
                        : <Copy size={12} />}
                      {captionCopied ? "Caption copied" : "Copy caption"}
                    </button>
                    <button
                      onClick={() => setCaptionIdx((i) => (i + 1) % captionPool.length)}
                      className="btn-ghost text-xs"
                      data-testid="hero-next-caption">
                      <Shuffle size={12} /> Another one
                    </button>
                    <span className="text-[10px] text-[#5a5050] ml-auto">
                      {(captionIdx % captionPool.length) + 1} / {captionPool.length}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 flex-wrap">
                <button onClick={dismiss} className="btn-primary"
                  data-testid="hero-enter-after-reserve">
                  Enter the site →
                </button>
                <a href="/leaderboard" className="btn-ghost text-xs"
                  data-testid="hero-leaderboard-link">
                  <Trophy size={12} /> View leaderboard
                </a>
                <a href="/whodunit-week" className="btn-ghost text-xs"
                  data-testid="hero-whodunit-link">
                  <Shield size={12} /> Whodunit Week
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Final-wave ambient glow */}
      {atFinal && (
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 50%, #d4af3722 0%, transparent 55%)",
            animation: "heroGlow 4s ease-in-out infinite",
          }} />
      )}
    </div>
  );
};

export default HeroCinematic;
